import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  type ButtonProps,
  Card,
  EditableTitleField,
  Icon,
  Text,
  InlineRowActions,
  useInlineRowActionChrome,
  useIsTouchDevice,
  cn,
} from "@salestrak/ui";
import type { IssueRow } from "#app/lib/data";
import { AgeLabel, showsUpdatesOffGhost, updateActionTooltip } from "#app/components/age/age-label";
import { AutomationTouchMarker } from "#app/components/automations/automation-touch-marker";
import { clockRunning } from "#app/lib/project-updates";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { ProjectStartFollowUpDialog } from "#app/components/updates/project-start-follow-up-dialog";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useEntityDetail } from "#app/components/entity";
import { useIssueRenameOptional } from "#app/components/issues/issue-rename-context";
import { useIssueView, useOpenIssueId } from "#app/components/issue-view";
import { pluralize } from "#app/lib/utils/pluralize";
import { IssueCardAssigneeSelector } from "./issue-card-assignee-selector";
import {
  getCardCustomFields,
  IssueCardFields,
  OWNS_PRESS_ATTR,
  cardListRowGap,
  sortByItemOrder,
  type CardCustomField,
  type CardFieldOps,
} from "./issue-card-fields";
import {
  hasExtraPropertyValue,
  IssueCardExtraProperty,
  type ExtraPropertyKey,
} from "./issue-card-property-chip";
import { IssueCardTeams, IssueCardTeamsAction } from "./issue-card-teams";
import { IssueCardAddress } from "./issue-card-address";
import { CardActionTooltip } from "./issue-card-action-tooltip";
import { CARD_ROW_CONTAINER_CLASS } from "./card-label-rail";
import { AddressPropertyInput } from "#app/components/issue-view/address-property-input";
import { IssueMenu } from "#app/components/issue-actions/issue-menu";
import type { Omittable } from "@salestrak/ui";
import { useIssueCardContext } from "./issue-card-context";

// pv2's card edge pair (Spencer 2026-08-05: the kanban card takes pv2's
// hover grammar over the app-wide wash — the ONE sanctioned exception to
// "interaction states are washes"). Light mode: no border — a hairline RING
// shadow + whisper drop carry the edge (softer than the flat border hairline
// against the sunken column well), and hover DEEPENS the drop (pv2's
// `card`/`card-hover` matched pair). Dark mode: the column well is `sunken`,
// 6.7 L under the card, so the FILL separates the card and the edge only
// has to define the corner — it rests on the page's own hairline rung
// (`--page-border`, +6.6 L over the card) rather than `card-border` (+9.7),
// which was tuned for a card whose fill sat 1.3 L over its column and had
// nothing but the line to say where it ended. Inside `surface="card"` the
// scoped `--border` IS card-border, so the page rung is named directly. No
// strengthening on hover. Chips and tokens keep their own interaction washes.
const CARD_EDGE =
  "transition-[border-color,box-shadow] dark:border-(--page-border) " +
  // The light border is TRANSPARENT, so the fill must paint under it —
  // Card's own not-dark:bg-clip-padding would leave a 1px see-through ring
  // between the fill and the shadow hairline.
  "not-dark:border-transparent not-dark:bg-clip-border " +
  "not-dark:shadow-[0_0_1px_0_--theme(--color-black/27%),0_1px_2px_0_--theme(--color-black/5%)] " +
  "not-dark:hover:shadow-[0_0_1px_0_--theme(--color-black/27%),0_1px_3px_0_--theme(--color-black/10%),0_1px_2px_-1px_--theme(--color-black/10%)] " +
  // pv2 cards are FLAT — no inner top-light; Card's RING_HIGHLIGHT is the
  // coss panel signature and stays off the kanban card.
  "before:shadow-none dark:before:shadow-none";

// A card outside the board (the map infobox: the whole card opens the
// project) says so on hover and carries its own pointer cursor — on the board
// the KanbanCard wrapper owns the cursor. Light already deepens the drop in
// CARD_EDGE; dark has no drop to deepen, so its hairline steps up to the
// strong border — the checkbox/radio hover rung, the quietest edge change
// the theme names.
const CARD_PRESSABLE = "cursor-pointer dark:hover:border-border-strong";

// The card whose project the issue view is showing (Asana: the open task's
// card stays lit beside the detail). It wears an accent EDGE only — no fill
// (Spencer 2026-09-28: the selection fill on the open card was dropped so
// the edge alone says "this one is open", and the bulk-selection fill keeps
// meaning "in the set"). `--brand` is never-neutral, so the edge stays blue
// under the neutral default accent. Softened a touch in light, where it
// meets the card's near-white fill; the accent itself in dark.
// Last in the merge so it beats every rest and hover edge above (light's
// transparent border, dark's page rung, the popover surface's rails); named
// per mode (`not-dark:` / `dark:`) so tailwind-merge REPLACES those rather
// than losing to the `not-dark:` rule's higher specificity.
const CARD_OPEN_EDGE =
  "not-dark:border-brand/70 not-dark:hover:border-brand/70 dark:border-brand dark:hover:border-brand";

// A card element that OWNS its press (a token, a footer action, the
// assignee, the library's control cluster) is marked `data-owns-press`: the
// board's mouse-drag hook leaves a press that starts there alone, and the
// card's own press (open) never fires from it. The title carries NO hover
// underline (Spencer 2026-09-28: the card's fill, edge and cursor say
// "open"; an underline riding every card hover read as noise).
const OWNS_PRESS_PROPS = { [OWNS_PRESS_ATTR]: "" } as const;

/** A card footer action: a 24px squared row (Button size="xs") that reads its
 *  chrome from the capsule it is rendered into — a ghost on the capsule's
 *  hairline (`InlineRowActions chrome="chip-ghost"`), or the quiet outline on
 *  its own when it stands alone. A component, not a prop bundle: the chrome
 *  hook reads the capsule's context, so it has to run INSIDE the capsule. */
function FooterActionButton({
  children,
  ...props
}: Omit<ButtonProps, "variant" | "size" | "shape">) {
  const chrome = useInlineRowActionChrome();
  return (
    <Button
      variant={chrome.variant}
      className={chrome.className}
      size="xs"
      shape="square"
      {...props}
    >
      {children}
    </Button>
  );
}

/** Property-name tooltip label per extra property (matches CardActionTooltip
 *  usage for address/age). */
const EXTRA_PROPERTY_LABEL: Record<ExtraPropertyKey, string> = {
  stage: "Stage",
  workflow: "Workflow",
  projectType: "Project Type",
  created: "Created",
  updated: "Updated",
};

type IssueCardProps = Omittable<{
  issue: IssueRow;
  isDragOverlay?: boolean;
  /** Bulk-selection state, from the board's `renderCard` meta. */
  isSelected?: boolean;
  /** On a touch device, the project menu stands as a ⋯ in the card's
   *  top-right corner, where the board's hover cluster sits on a desktop —
   *  touch has no hover, no long press and no drag, so this is the card's
   *  one door to its actions there. The board's cards set it. */
  touchMenu?: boolean;
  /** The card itself is the press target — a hover affordance on its edge
   *  plus its own pointer cursor. Off on the board, where the KanbanCard
   *  wrapper carries the cursor and the edge stays quiet. */
  pressable?: boolean;
  /** The surface the card RESTS ON, when it is not the board. The same
   *  card re-grounds for its host the way `data-table surface` does: on a
   *  `popover` (the map infobox) the dark card lifts to the POPOVER's own
   *  fill over the zone's slightly-deeper ground, with the strong hairline —
   *  a gentle step up, never a dark slab (a page-rung fill there read as a
   *  shock; Spencer, 2026-09-09). Light keeps the card fill white over the
   *  zone's muted ground, edged by a real rail-rung border. */
  surface?: "popover";
}>;

export const IssueCard = React.memo(function IssueCard({
  issue,
  isDragOverlay,
  isSelected,
  touchMenu,
  pressable,
  surface,
}: IssueCardProps) {
  const {
    actions: {
      onAddressSelect,
      onAddressClear,
      onFieldValuesChange,
      onStageChange,
      onProjectTypeChange,
      onTeamsChange,
      onCreateOption,
      optionActions,
    },
    customFieldDefs,
    displayOptions,
    labelRail,
  } = useIssueCardContext();
  const { openIssue } = useIssueView();
  const isOpen = useOpenIssueId() === issue.issueId;
  const { openEntity } = useEntityDetail();
  const rename = useIssueRenameOptional();
  // A finger's hold on a card never opens the context menu's long press; the
  // board's cards carry a standing ⋯ instead (`touchMenu`).
  const isTouch = useIsTouchDevice();
  const showsTouchMenu = touchMenu && isTouch && !isDragOverlay;
  const isRenaming = rename != null && rename.renamingIssueId === issue.issueId;
  const [ageDialogOpen, setAgeDialogOpen] = useState(false);
  // Lifted so the ghost's "Updates off" tooltip can stand down while its
  // Updates popover is up.
  const [startFollowUpOpen, setStartFollowUpOpen] = useState(false);
  // Stop the click from bubbling to the KanbanCard's own onClick, which would
  // otherwise also fire openIssue() (defaulting to the summary tab) behind the
  // age dialog.
  const handleAgeClick = useCallback((e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setAgeDialogOpen(true);
  }, []);
  const handleViewProfile = useCallback(
    (userId: string) => openEntity("user", userId, { via: "card" }),
    [openEntity],
  );
  // Editing the card (read-only context menu) is gated on the server's own
  // edit decision — the role floor and the object's access level together —
  // so every editor the card offers is a write the server accepts. The drag
  // affordance lives on the library KanbanCard wrapper, which reads
  // `isItemDragDisabled` from the board.
  const editable = issue.viewerCanEdit;
  const dragDisabled = !editable;

  // Valued custom fields only — an empty custom field never shows on the
  // card (unlike properties, which keep footer icon actions when empty).
  const cardFields = useMemo(
    () =>
      getCardCustomFields(
        customFieldDefs,
        issue.values,
        displayOptions.showEmptyFields,
      ),
    [customFieldDefs, issue.values, displayOptions.showEmptyFields],
  );
  const fieldOps = useMemo<CardFieldOps>(
    () => ({
      onValuesChange: (field, value) => onFieldValuesChange(issue, field, value),
      onCreateOption,
      optionActions,
    }),
    [onFieldValuesChange, issue, onCreateOption, optionActions],
  );

  // A hidden property disappears everywhere — its valued row and its footer
  // affordance use the same display gate.
  const showAddressRow =
    displayOptions.showAddress && !!issue.address?.wholeAddress;
  const showTeamsRow =
    displayOptions.showTeams && (issue.teams?.length ?? 0) > 0;

  // Extra body properties (stage/type/created/updated) — gated on their
  // display flag AND a present value (like address, never "—").
  const extraProps = useMemo(
    () =>
      (
        [
          ["stage", displayOptions.showStage],
          ["workflow", displayOptions.showWorkflow],
          ["projectType", displayOptions.showProjectType],
          ["created", displayOptions.showCreated],
          ["updated", displayOptions.showUpdated],
        ] as const
      ).filter(([key, on]) => on && hasExtraPropertyValue(key, issue)),
    [
      displayOptions.showStage,
      displayOptions.showWorkflow,
      displayOptions.showProjectType,
      displayOptions.showCreated,
      displayOptions.showUpdated,
      issue,
    ],
  );

  // Body render order = the popover's dragged order. Consecutive fields
  // chunk into one token block so wrap layout keeps flowing; properties
  // (the address row and the extra property chips) interleave between chunks
  // wherever the user dropped them.
  type BodyItem =
    | { kind: "address" }
    | { kind: "teams" }
    | { kind: "extra"; propertyKey: ExtraPropertyKey }
    | { kind: "fields"; fields: CardCustomField[] };
  const bodyItems = useMemo<BodyItem[]>(() => {
    const entries: {
      key: string;
      item:
        | Exclude<BodyItem, { kind: "fields" }>
        | { kind: "field"; field: CardCustomField };
    }[] = [];
    if (showAddressRow)
      entries.push({ key: "address", item: { kind: "address" } });
    if (showTeamsRow) entries.push({ key: "teams", item: { kind: "teams" } });
    for (const [key] of extraProps) {
      entries.push({ key, item: { kind: "extra", propertyKey: key } });
    }
    for (const f of cardFields) {
      entries.push({
        key: f.field.customFieldId,
        item: { kind: "field", field: f },
      });
    }
    const ordered = sortByItemOrder(
      entries,
      (e) => e.key,
      displayOptions.itemOrder ?? [],
    );
    const items: BodyItem[] = [];
    for (const { item } of ordered) {
      if (item.kind === "field") {
        const last = items[items.length - 1];
        if (last?.kind === "fields") last.fields.push(item.field);
        else items.push({ kind: "fields", fields: [item.field] });
      } else {
        items.push(item);
      }
    }
    return items;
  }, [showAddressRow, showTeamsRow, extraProps, cardFields, displayOptions.itemOrder]);

  // Hover previews are desktop-only. On mobile (touch) there is no hover, and
  // wrapping the button in a Popover would make a tap both toggle the popover
  // and fire the click — so on mobile we render the bare buttons and let their
  // own handlers (open the age dialog / open the activity tab) run. The
  // ghost's Updates popover is the exception on purpose: it's press-open and
  // the ghost has no action of its own, so touch shares it.
  const { stageMap, workflowMap } = useWorkspaceData();
  const cardStage = issue.stageId ? stageMap.get(issue.stageId) : undefined;
  const cardWorkflow = cardStage?.workflowId
    ? workflowMap.get(cardStage.workflowId)
    : undefined;
  const hasAge = clockRunning(issue.age, cardStage, cardWorkflow);
  // No live clock — updates are off somewhere up the ladder (the Project's own
  // opt-out, a Stage off-pin, an off Workflow, or a closed Stage's default) —
  // so the card shows the ghost pulse instead of a blank slot.
  // The ghost is a START affordance, not a state: it exists only for a viewer
  // who can press it. Read-only viewers get absence, the same nothing every
  // list and table shows for a Project with no clock.
  const showsGhost =
    editable && !hasAge && showsUpdatesOffGhost(issue.age, cardStage, cardWorkflow);
  // The footer's leading capsule holds: the follow-up clock or its
  // updates-off ghost, the empty-address and empty-teams picker triggers,
  // and the comments indicator. Mirrors the render conditions below, so the
  // capsule never mounts around nothing.
  const hasFooterActions =
    (displayOptions.showAge && (hasAge || showsGhost)) ||
    (displayOptions.showAddress &&
      !issue.address?.wholeAddress &&
      !dragDisabled) ||
    (displayOptions.showTeams &&
      (issue.teams?.length ?? 0) === 0 &&
      !dragDisabled) ||
    issue.noteCount > 0;

  // Card footer actions are 24px rows (Button size="xs") fused into ONE
  // hairline capsule (`InlineRowActions chrome="chip-ghost"`: the outline-
  // ghost recipe — hairline, no fill swap against the card — drawn once
  // around the cluster instead of once per action). Inside it every action
  // is a ghost on the capsule; the assignee avatar stays bare outside it.
  const ageButton = (
    <FooterActionButton onClick={handleAgeClick}>
      {/* `size={null}`: the label inherits the xs rung's 12px font. */}
      <AgeLabel age={issue.age} stageId={issue.stageId} size={null} showOff />
    </FooterActionButton>
  );

  // The ghost opens the Start Follow up dialog; the press must not bubble to
  // the KanbanCard's onClick, which would open the issue under the dialog.
  const ghostButton = (
    <FooterActionButton
      onClick={(e) => {
        e.stopPropagation();
        setStartFollowUpOpen(true);
      }}
    >
      {/* `size={null}`: the label inherits the xs rung's 12px font. */}
      <AgeLabel age={issue.age} stageId={issue.stageId} size={null} showOff />
    </FooterActionButton>
  );

  // Comments are an ACTIVITY INDICATOR, not a set-able property — it rides
  // the footer's shared capsule like every other footer action. Attention
  // rides the unread channel (solid glyph + bright ink + bold count), not the
  // border; the tooltip carries the figure the chip does not: "3 unread"
  // while lit (unread counts replies; the chip's count is threads only, so
  // the two never sit in one sentence), "2 comments" at rest. Unread clears
  // note by note as each scrolls into view on the Activity tab
  // (useMarkNotesRead) or from the inbox.
  // ICON GAP: `[&_path]:fill-current` paints the outline `message` artwork
  // solid until a `message-filled` icon is authored in tinySVG.
  const noteButton = (
    <FooterActionButton
      onClick={(e) => {
        // Stop the click bubbling to the KanbanCard's onClick, which fires
        // openIssue() with no options and would reset the tab to summary.
        e.stopPropagation();
        openIssue(issue, {
          initialTab: "activity",
          focusUnread: issue.hasUnreadNote,
          source: "card",
        });
      }}
    >
      <Icon
        name="message"
        size="md"
        color={issue.hasUnreadNote ? undefined : "muted"}
        className={issue.hasUnreadNote ? "[&_path]:fill-current" : undefined}
      />
      {/* `size={null}`: the count inherits the xs rung's 12px font. */}
      <Text
        size={null}
        variant={issue.hasUnreadNote ? "default" : "muted"}
        weight={issue.hasUnreadNote ? "bold" : undefined}
      >
        {issue.noteCount}
      </Text>
    </FooterActionButton>
  );
  const noteLabel = issue.hasUnreadNote
    ? `${issue.unreadNoteCount} unread`
    : `${issue.noteCount} ${pluralize("comment", { count: issue.noteCount })}`;

  const textLabels = displayOptions.fieldLabelMode === "text";
  // List layout = one full-width token per line: the tooltip wrappers around
  // the icon-mode property tokens must stretch too, or the token hugs its
  // content inside a wrapper that does not fill the row.
  const listLayout = displayOptions.fieldLayout === "list";
  // The body inset pulls to 4px so the rows' hover wash reaches nearly to the
  // card edge; the title rail and footer add the 4px back to keep their seats,
  // and list rows tighten the 4px object rhythm to 2px (28px rows, 30px pitch).
  const bareList = listLayout;
  // Stacked text labels breathe more between rows (cardListRowGap).
  const listRowGap = cardListRowGap(
    displayOptions.fieldLabelMode,
    displayOptions.textLabelLayout,
    displayOptions.fieldLayout,
  );

  const cardContent = (
    <Card
      data-testid={`issue-card-${issue.issueId}`}
      // While this card's title is in inline edit, mark the surface so the
      // board's hover cluster (pencil, menu, checkbox) clears the field
      // entirely (KanbanCard reads this off the card group).
      data-card-editing={isRenaming ? "" : undefined}
      // One step above the column's card-list area, so the stack reads as
      // cards resting in a well rather than panels flush with it. Hover lives
      // on the EDGE (see CARD_EDGE) — no wash on the card fill.
      surface="card"
      // Only bulk selection fills the card; the open card carries an edge
      // alone (see CARD_OPEN_EDGE).
      selected={isSelected}
      // The open project's card, for callers and tests.
      data-open={isOpen ? "" : undefined}
      minWidth={0}
      radius="sm"
      shrink
      position={showsTouchMenu ? "relative" : undefined}
      className={cn(
        CARD_EDGE,
        pressable && CARD_PRESSABLE,
        // On the popover, light draws a real border at the popover's own rail
        // rung, not CARD_EDGE's shadow ring: the infobox scroll viewport
        // clips an outside shadow flat along every edge, leaving only the
        // corner arcs. Dark's hairline steps to border-strong — the card fill
        // is the popover's own, and the default border vanished on it.
        surface === "popover" &&
          "not-dark:border-border not-dark:shadow-none not-dark:hover:shadow-none dark:border-border-strong dark:bg-popover",
        // The pressable popover card's hover cue is one edge step up from its
        // own rest rung: light border → border-strong, dark border-strong →
        // an ink-alpha line (there is no stronger border token).
        surface === "popover" &&
          pressable &&
          "not-dark:hover:border-border-strong dark:hover:border-foreground/25",
        isOpen && CARD_OPEN_EDGE,
      )}
    >
      <Box
        direction="column"
        paddingX="xs"
        paddingBottom="sm"
      >
        {/* Title rail — 40px for a single line, hugging the card's top edge.
            The 28px action cluster floats over it, 4px from the top, and
            reserves NO width: the title runs the card's full width and the
            cluster's own popover backing covers whatever it overlaps
            (KanbanCard). The title sits on a fixed 10px inset above and below
            (no centering): a wrapped title keeps its first line where a
            single-line one sits and pushes the body down instead of climbing
            toward the edge. The rail carries the extra 4px leading inset (8px
            in bare mode, where the body inset drops to 4px). Body rows and
            the footer sit at the card's 8px padding. 10px has no spacing
            token — measured gap. */}
        <Box
          direction="column"
          paddingLeft="sm"
          // The standing ⋯ reserves its seat, so the title wraps before it
          // instead of running under it — held through an in-place rename,
          // so entering the edit re-wraps nothing.
          paddingRight={showsTouchMenu ? "xl" : undefined}
          className="py-2.5"
        >
          {isRenaming ? (
            // The card menu's Rename edits the title in place — same size and
            // weight as the resting title, so entering and leaving edit mode
            // moves no glyph. flushLeft folds the field's own hover inset back
            // onto the rail's 4px, keeping the text at the resting x.
            <EditableTitleField
              value={issue.name}
              size="sm"
              weight="medium"
              fill
              flushLeft
              editing
              // The resting title wraps (truncate={2}); the edit keeps those
              // line boxes instead of collapsing to a one-line scroll.
              multiline
              // No editing ring — the card is the frame, and the swap to a
              // caret is the editing signal.
              chrome="none"
              aria-label={`Rename ${issue.name}`}
              onCommit={(next) => rename.onRenameCommit(issue, next)}
              onEditingChange={(editing) => {
                if (!editing) rename.onRenamingEnd();
              }}
            />
          ) : (
            // Carry the SAME vertical padding the EditableTitleField wears
            // (paddingY="2xs"), so the resting title and the in-place edit are
            // both 24px and entering edit grows no row. No paddingX: the rail's
            // paddingLeft already seats the text where the field's flushLeft
            // lands it.
            <Box align="start" direction="row" gap="xs" paddingY="2xs">
              {/* The title is the card's open trigger, at rest ink with no
                  hover cue of its own: the cursor comes from the KanbanCard
                  wrapper (pointer wherever a press opens the card), and the
                  tokens below it light on hover to say "edit". */}
              <Text
                size="sm"
                weight="medium"
                truncate={2}
              >
                {issue.name}
              </Text>
              <AutomationTouchMarker issueId={issue.issueId} issueName={issue.name} />
            </Box>
          )}
        </Box>

        {/* Body objects in the user's dragged order (address row, extra
            property chips, field-token chunks — the display popover's Shown
            list). First object rides the rail's whitespace (no lead-in);
            everything stacked after runs the 4px object rhythm (2px in bare
            list mode, the rows' own gap). In TEXT label
            mode ("Field labels") the rows wear the shared name-labeled token
            shell and the on-screen name retires the property tooltip. */}
        {bodyItems.map((item, index) => {
          const first = index === 0;
          if (item.kind === "address") {
            return (
              <Box
                key={`address-${index}`}
                direction="row"
                minWidth={0}
                paddingTop={first ? undefined : bareList ? listRowGap : "xs"}
                // The row is the query container the text-mode label rail's
                // cap resolves against (card-label-rail.ts).
                className={CARD_ROW_CONTAINER_CLASS}
              >
                {/* Edits INLINE in its token — the property grammar every
                    address surface shares; the token carries its own name
                    tooltip in icon mode. */}
                <IssueCardAddress
                  issue={issue}
                  canApply={!dragDisabled}
                  onSelect={(wholeAddress) => onAddressSelect(issue, wholeAddress)}
                  onClear={() => onAddressClear(issue)}
                  labelMode={displayOptions.fieldLabelMode}
                  labelLayout={displayOptions.textLabelLayout}
                  layout={displayOptions.fieldLayout}
                  labelRail={labelRail}
                />
              </Box>
            );
          }
          if (item.kind === "teams") {
            const teamsChip = (
              <IssueCardTeams
                issue={issue}
                canApply={!dragDisabled}
                onTeamsChange={onTeamsChange}
                labelMode={displayOptions.fieldLabelMode}
                labelLayout={displayOptions.textLabelLayout}
                layout={displayOptions.fieldLayout}
                labelRail={labelRail}
              />
            );
            return (
              <Box
                key={`teams-${index}`}
                direction="row"
                minWidth={0}
                paddingTop={first ? undefined : bareList ? listRowGap : "xs"}
                className={CARD_ROW_CONTAINER_CLASS}
              >
                {textLabels ? (
                  teamsChip
                ) : (
                  <CardActionTooltip label="Teams">{teamsChip}</CardActionTooltip>
                )}
              </Box>
            );
          }
          if (item.kind === "extra") {
            const extraChip = (
              <IssueCardExtraProperty
                propertyKey={item.propertyKey}
                issue={issue}
                canApply={!dragDisabled}
                onStageChange={(stageId) => onStageChange(issue, stageId)}
                onProjectTypeChange={(issueTypeId) =>
                  onProjectTypeChange(issue, issueTypeId)
                }
                labelMode={displayOptions.fieldLabelMode}
                labelLayout={displayOptions.textLabelLayout}
                layout={displayOptions.fieldLayout}
                labelRail={labelRail}
              />
            );
            return (
              // No wrapper paddingX — the token sits flush in the body column
              // like the address row.
              <Box
                key={`extra-${item.propertyKey}-${index}`}
                direction="row"
                minWidth={0}
                paddingTop={first ? undefined : bareList ? listRowGap : "xs"}
                className={CARD_ROW_CONTAINER_CLASS}
              >
                {textLabels ? (
                  extraChip
                ) : (
                  <CardActionTooltip label={EXTRA_PROPERTY_LABEL[item.propertyKey]}>
                    {extraChip}
                  </CardActionTooltip>
                )}
              </Box>
            );
          }
          return (
            <IssueCardFields
              key={`fields-${index}`}
              fields={item.fields}
              lead={first ? "none" : "item"}
              canApply={!dragDisabled}
              fieldOps={fieldOps}
              labelMode={displayOptions.fieldLabelMode}
              labelLayout={displayOptions.textLabelLayout}
              layout={displayOptions.fieldLayout}
              labelRail={labelRail}
            />
          );
        })}

        {/* Footer. Spacing separates it from valued properties without a
            divider. Action order is FIXED (Spencer): updates · address ·
            comments, assignee far right. The 28px minimum centers the
            capsule (24px actions + the capsule's 1px hairline and inset)
            without adding a separating rule. The capsule mounts only when
            it has an action to hold — an empty hairline is a dead box. */}
        <Box
          direction="row"
          align="center"
          justify="between"
          paddingTop={bodyItems.length > 0 ? "md" : "sm"}
          paddingX="xs"
          minHeight={28}
          gap="sm"
        >
          {hasFooterActions ? (
          <InlineRowActions
            chrome="chip-ghost"
            visibility="always"
            shrink
            {...OWNS_PRESS_PROPS}
          >
            {/* Follow up is the one toggleable footer resident (display
                options, default on) — hidden = chip AND ghost both go. The two
                never coexist: a live clock owns the slot, and only when there
                is none does the updates-off ghost take it. */}
            {!displayOptions.showAge ? null : hasAge ? (
              // The tooltip names the press, as every footer action's does,
              // and says when the update is owed.
              <CardActionTooltip label={updateActionTooltip(issue.age, cardStage, cardWorkflow)}>
                {ageButton}
              </CardActionTooltip>
            ) : showsGhost ? (
              // Updates-off Projects — closed columns included — keep a ghost
              // pulse in the slot the chip otherwise owns: its press opens the
              // Start Follow up dialog for this one Project, and the tooltip
              // names that press, as the running chip's names "Update".
              <CardActionTooltip label="Start Follow up" disabled={startFollowUpOpen}>
                {ghostButton}
                <ProjectStartFollowUpDialog
                  via="card_ghost"
                  issueId={issue.issueId}
                  age={issue.age}
                  stage={cardStage}
                  workflow={cardWorkflow}
                  open={startFollowUpOpen}
                  onOpenChange={setStartFollowUpOpen}
                />
              </CardActionTooltip>
            ) : null}
            {/* Address-less cards keep an icon-only picker trigger here
                (the reference empty-property pattern) — there is no value
                row to edit in yet; once set, the address row under the title
                edits inline. */}
            {displayOptions.showAddress &&
            !issue.address?.wholeAddress &&
            !dragDisabled ? (
              <CardActionTooltip label="Address">
                <AddressPropertyInput
                  wholeAddress={issue.address?.wholeAddress}
                  onSelect={(wholeAddress) =>
                    onAddressSelect(issue, wholeAddress)
                  }
                  variant="card"
                  readOnly={dragDisabled}
                />
              </CardActionTooltip>
            ) : null}
            {/* Team-less cards keep an icon-only picker trigger here (the
                address action's empty-property pattern); once set, the teams
                row under the title owns the picker instead. */}
            {displayOptions.showTeams &&
            (issue.teams?.length ?? 0) === 0 &&
            !dragDisabled ? (
              <CardActionTooltip label="Teams">
                <IssueCardTeamsAction issue={issue} onTeamsChange={onTeamsChange} />
              </CardActionTooltip>
            ) : null}
            {/* "Comments" in user-facing copy — the notes name is
                backend/internal only. No hover preview: the tooltip names the
                property with its counts and the press opens the Activity tab. */}
            {issue.noteCount > 0 && (
              <CardActionTooltip label={noteLabel}>{noteButton}</CardActionTooltip>
            )}
          </InlineRowActions>
          ) : (
            <Box />
          )}

          {/* Assignee selector */}
          <Box
            direction="column"
            shrink={false}
            align="center"
            {...OWNS_PRESS_PROPS}
          >
            <CardActionTooltip label="Assignee">
              <IssueCardAssigneeSelector
                issueId={issue.issueId}
                assignees={issue.assignees ?? []}
                onViewProfile={handleViewProfile}
                viewerAccessLevel={issue.viewerAccessLevel}
              />
            </CardActionTooltip>
          </Box>
        </Box>
      </Box>
      {showsTouchMenu && !isRenaming ? (
        // The desktop cluster's seat: its ⋯ lands 7px in from the card's
        // outer corner, 6px inside this 1px border. The wrapper owns the
        // press, so opening the menu never also opens the card.
        <Box
          position="absolute"
          top={6}
          right={6}
          {...OWNS_PRESS_PROPS}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          <IssueMenu
            issue={issue}
            type="dropdown"
            triggerShape="pill"
            readOnly={!editable}
          />
        </Box>
      ) : null}
    </Card>
  );

  return (
    <>
      {isDragOverlay ? (
        cardContent
      ) : (
        <IssueMenu
          issue={issue}
          type="context"
          disabled={isTouch}
          readOnly={dragDisabled}
        >
          {cardContent}
        </IssueMenu>
      )}
      <UpdateAgeDialog
        open={ageDialogOpen}
        onOpenChange={setAgeDialogOpen}
        issueId={issue.issueId}
        stageId={issue.stageId}
        age={issue.age}
        via="card"
      />
    </>
  );
});
